import {
  Box,
  Button,
  HStack,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Input,
  PaginatedTable,
  Select,
  Spinner,
} from "../../../components/common";
import ReActivateDPUser from "../../../components/GlobalDashboard/ReActivateUser/ReActivateDPUser";
import ResetPassword from "../../../components/GlobalDashboard/ResetPassword/ResetPassword";
import SuspendDPUser from "../../../components/GlobalDashboard/SuspendUser/SuspendDPUser";
import {
  UserData,
  getUserMenuHeaders,
  mapTableData,
} from "../../../components/GlobalDashboard/UserUtils";
import { SearchIcon } from "../../../components/icons/svg";
import { fetchUsersDP } from "../../../utils/api/users.api";
import { useUserInfo } from "../../../utils/hooks/useUserInfo";
import CreateDPUser from "./CreateUser/CreateDPUser";
import EditDPUser from "./EditUser/EditDPUser";

//dealership portal -> Users tab
const DPUsers = () => {
  const dealerId = useUserInfo("dealership");
  const navigate = useNavigate();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [usersCount, setUsersCount] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(true);
  const [clickedRow, setClickedRow] = useState<UserData>({
    id: 0,
    uuid:0,
    logged_ip: "",
    full_name: "",
    last_login: "",
    access_level: "",
    created_at: "",
    status: "",
    dealerships: [],
    email: "",
    time_zone: "",
  });
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(20);

  const [searchValue, setSearchValue] = useState<string>("");

  const handleSelect = (option: SelectOptionProps) => {
    setActiveModal(option.value);
  };
  useEffect(() => {
    if (activeModal === "settings") {
      navigate(`/users/settings/${clickedRow.id}`);
    }
  }, [activeModal]);
  const ROLE_TITLES = "Owner/GM,Manager/Admin,Sales Person,BDC Agent";

  const optionsHeader: SelectOptionProps[] = [
    { label: "All Access Levels", value: ROLE_TITLES },
    { label: "Owner/GM", value: "Owner/GM" },
    { label: "Manager/Admin", value: "Manager/Admin" },
    { label: "Sales person", value: "Sales Person" },
    { label: "BDC Agent", value: "BDC Agent" },
  ];

  const [selectedOption, setSelectedOption] =
    useState<SelectOptionProps | null>(null);

  const [tableHeader, setTableHeader] = useState(
    getUserMenuHeaders(isActive, true, true)
  );

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["fetchUsersDP", dealerId, isActive, page, perPage],
    queryFn: () => {
      const queryParameters: any = {
        dealership_id: dealerId?.id,
        status: isActive ? "active" : "suspended",
        role_titles: selectedOption?.value || ROLE_TITLES,
        searchValue: searchValue,
      };

      if (!searchValue) {
        queryParameters.page = page;
        queryParameters.per_page = perPage;
      }

      return fetchUsersDP(queryParameters);
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
  const optionsActive: SelectOptionProps[] = [
    { label: "Edit User", value: "editUser" },
    { label: "Reset Password", value: "resetPassword" },
    { label: "Suspend", value: "suspend" },
    { label: "Settings", value: "settings" },
  ];
  const optionsSuspended: SelectOptionProps[] = [
    { label: "Edit User", value: "editUser" },
    { label: "Reset Password", value: "resetPassword" },
    { label: "Re-Activate User", value: "reActivate" },
    { label: "Settings", value: "settings" },
  ];

  const activeData =
    data &&
    data.users?.map((item: UserData) =>
      mapTableData(
        item,
        handleSelect,
        isActive ? optionsActive : optionsSuspended
      )
    );

  const [tableData, setTableData] = useState<any>(activeData);

  //...............use effects..................
  useEffect(() => {
    refetch();
  }, [refetch, searchValue, selectedOption]);

  useEffect(() => {
    setTableHeader(getUserMenuHeaders(isActive, true, true));
    setTableData(activeData);

    setUsersCount(data?.users_count);
  }, [data, isActive]);

  useEffect(() => {
    resetPagination();
  }, [isActive]);

  const resetPagination = () => {
    setPage(1);
    setPerPage(20);
  };
  return (
    <>
      <VStack
        w="100%"
        alignItems="flex-start"
        gap="0"
        overflow="auto"
        h="calc(100vh - 172px)"
      >
        <HStack
          w="100%"
          justifyContent="space-between"
          padding="1.25rem 1.5rem"
          background="white"
        >
          <HStack gap="0.625rem" w="100%" justifyContent="flex-start">
            <Input
              maxW="20rem"
              type="text"
              hasIcon={true}
              isIconRight={false}
              icon={<SearchIcon />}
              placeholder="Type here to search"
              value={searchValue}
              onChange={(e: any) => {
                setSearchValue(e.target.value);
                setPage(1);
              }}
            />
            <VStack p="relative" zIndex="3">
              <Select
                onChange={(value: any) => {
                  setSelectedOption(value);
                  resetPagination();
                }}
                placeholder="All Access Levels"
                options={optionsHeader}
                variant="default"
                value={selectedOption?.value}
                w={"240px"}
              />
            </VStack>

            <HStack border="1px solid var(--grey-300)" borderRadius="0.5rem">
              <Button
                variant="none"
                padding="0.5rem 1rem"
                onClick={() => setIsActive(true)}
                borderRadius="0.5rem"
                h="100%"
                fontWeight="500"
                background={isActive ? "var(--secondary-600)" : "white"}
                color={isActive ? "white" : "var(--grey-900)"}
              >
                Active
              </Button>
              <Button
                variant="none"
                h="100%"
                padding="0.5rem 1rem"
                borderRadius="0.5rem"
                fontWeight="500"
                onClick={() => setIsActive(false)}
                color={!isActive ? "white" : "var(--grey-900)"}
                background={!isActive ? "var(--secondary-600)" : "white"}
              >
                Suspended
              </Button>
            </HStack>
          </HStack>
          <Button onClick={onOpen}>Create User</Button>
        </HStack>
        <VStack w="100%" alignItems="flex-start" padding="1.5rem">
          <Box
            w="100%"
            alignItems="flex-start"
            background="white"
            border="1px solid var(--grey-300)"
            borderRadius="0.5rem"
          >
            <Text
              textStyle="caption"
              padding="1.5rem 1rem 0.75rem"
              w="100%"
              color="var(--grey-600)"
            >
              Users found ({usersCount})
            </Text>
            <PaginatedTable
              header={tableHeader}
              data={tableData}
              itemsPerPage={perPage}
              maxPageNumbers={3}
              isPaginated={true}
              lastCenter={false}
              hasMultiBody={false}
              rowOnClick={(rowItem) => {
                setClickedRow(rowItem);
              }}
              perPageChange={(value) => {
                setPage(1);
                setPerPage(Number(value?.label));
              }}
              currentPage={page}
              setCurrentPage={setPage}
              rowCount={usersCount}
              isLoadingData={isLoading}
            />
          </Box>
        </VStack>

        {isOpen && (
          <CreateDPUser
            isOpen={isOpen}
            onClose={onClose}
            refetchParent={refetch}
          ></CreateDPUser>
        )}

        {activeModal === "editUser" && (
          <EditDPUser
            isOpen={true}
            onClose={() => setActiveModal(null)}
            refetchParent={refetch}
            id={clickedRow?.id}
          />
        )}

        {activeModal === "resetPassword" && (
          <ResetPassword
            isOpen={true}
            onClose={() => setActiveModal(null)}
            refetchParent={refetch}
            id={clickedRow?.id}
          />
        )}
        {activeModal === "suspend" && (
          <SuspendDPUser
            isOpen={true}
            onClose={() => setActiveModal(null)}
            userId={clickedRow?.id}
            refetchParent={refetch}
            selectedUser={clickedRow}
          ></SuspendDPUser>
        )}
        {activeModal === "reActivate" && (
          <ReActivateDPUser
            isOpen={true}
            onClose={() => setActiveModal(null)}
            userId={clickedRow?.id}
            refetchParent={refetch}
          ></ReActivateDPUser>
        )}
      </VStack>
    </>
  );
};

export default DPUsers;
